import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { ShowCard } from "@/components/ShowCard";
import { Drama, Search, Ticket } from "lucide-react";
import { Seo } from "@/components/Seo";

const FILTERS = ["All", "Now Playing", "Coming Soon", "Closing Soon", "Closed"];

export default function BrowseShows() {
  const [shows, setShows] = useState(null);
  const [filter, setFilter] = useState("All");
  const [q, setQ] = useState("");

  useEffect(() => {
    const query = filter === "All" ? "" : `?status=${encodeURIComponent(filter)}`;
    api.get(`/shows${query}`).then((r) => setShows(r.data)).catch(() => setShows([]));
  }, [filter]);

  const filtered = useMemo(() => {
    if (!shows) return null;
    const term = q.trim().toLowerCase();
    if (!term) return shows;
    return shows.filter(
      (s) => s.name.toLowerCase().includes(term) || (s.venue || "").toLowerCase().includes(term)
    );
  }, [shows, q]);

  const heroImages = (shows || []).filter((s) => s.image_url).slice(0, 4);

  return (
    <div>
      <Seo
        title="Browse West End shows"
        description="Every West End show in one place. Compare official ticket sellers, check booking dates and find the best value for your seat and date."
/>
      <section className="relative overflow-hidden border-b border-amber-900/20">
        <div className="absolute inset-0 flex">
          {heroImages.length > 0 ? (
            heroImages.map((s, i) => (
              <img key={i} src={s.image_url} alt="" className="h-full w-1/4 scale-125 object-cover opacity-60 blur-2xl" />
            ))
          ) : (
            <div className="h-full w-full bg-[#161118]" />
          )}
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0D0A0E]/50 via-[#0D0A0E]/75 to-[#0D0A0E]" />
        <div className="grain absolute inset-0" />
        <div className="relative z-10 mx-auto max-w-7xl px-4 py-10 sm:px-6 sm:py-14">
          <p className="overline-label text-[#C89B3C]">Now on in the West End</p>
          <h1 className="mt-2 font-serif text-4xl font-semibold text-amber-50 sm:text-5xl">Browse Shows</h1>
          <div className="mt-5 flex items-start gap-3 rounded-2xl border border-[hsl(var(--gold))]/30 bg-white/5 px-5 py-4 backdrop-blur-sm">
            <Ticket className="mt-1 h-5 w-5 flex-shrink-0 text-[hsl(var(--gold))]" />
            <p className="text-base text-amber-100/80">
              <span className="font-semibold text-amber-50">Compare before you book.</span> Most shows link to several
              official ticket sellers, each with its own starting price — check a couple before you buy to find the
              best value for your seat and date.
            </p>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6">

      <div className="mt-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`filter-${f.toLowerCase().replace(/ /g, "-")}`}
              onClick={() => setFilter(f)}
              className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                filter === f
                  ? "border-primary bg-primary text-primary-foreground"
                  : "border-border bg-card text-muted-foreground hover:border-primary hover:text-foreground"
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        <div className="relative lg:w-80">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            data-testid="show-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search shows or venues…"
            className="w-full rounded-full border border-input bg-card py-2.5 pl-10 pr-4 text-sm outline-none focus:ring-2 focus:ring-ring"
          />
        </div>
      </div>

      {filtered && filtered.length === 0 && (
        <div className="mt-10 rounded-2xl border border-dashed border-border bg-card p-12 text-center">
          <Drama className="mx-auto h-10 w-10 text-muted-foreground" />
          <h2 className="mt-4 font-serif text-2xl font-semibold">
            {q ? "No shows match your search" : "No shows yet"}
          </h2>
          <p className="mx-auto mt-2 max-w-md text-muted-foreground">
            {q ? "Try a different show or venue name." : "Shows are added by the admin. Once they're in the system, they'll appear here."}
          </p>
        </div>
      )}

{filtered && filtered.length > 0 && (
        <>
          <p className="mt-6 text-sm text-muted-foreground">{filtered.length} show{filtered.length !== 1 ? "s" : ""}</p>
          <div className="mt-3 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((s, i) => <ShowCard key={s.id} show={s} index={i} />)}
          </div>
        </>
      )}
      </div>
    </div>
  );
}
